import React, { useMemo, useState, useEffect } from "react";

import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";

import {
  ShoppingBagOutlined,
  CheckCircleOutline,
  LocalShippingOutlined,
  CancelOutlined,
  VisibilityOutlined,
  PrintOutlined,
  Search,
} from "@mui/icons-material";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { getCurrency } from "../../Utils/currency";

import {
  useGetOrdersByStatusQuery,
  useOrderApproveMutation,
} from "../../Store/Services/orderService";

import OrderDetailsDialog from "./OrderDetailsDialog";
import PrintOrderDetailsDialog from "./PrintOrderDetailsDialog";
import DeliverConfirmDialog from "./DeliverConfirmDialog";
import styles from "./OrderTabs.module.css";

const ORDER_TABS = [
  {
    value: "placed",
    label: "Placed Orders",
    statusId: 5,
    icon: <ShoppingBagOutlined />,
  },
  {
    value: "approved",
    label: "Approved Orders",
    statusId: 6,
    icon: <CheckCircleOutline />,
  },
  {
    value: "delivered",
    label: "Delivered Orders",
    statusId: 7,
    icon: <LocalShippingOutlined />,
  },
  {
    value: "rejected",
    label: "Rejected Orders",
    statusId: 10,
    icon: <CancelOutlined />,
  },
];

const LOAD_COUNT = 12;

const DATE_FILTERS = [
  { key: "today", label: "Today" },
  { key: "all", label: "All Dates" },
  { key: "yesterday", label: "Yesterday" },
  { key: "lastWeek", label: "Last Week" },
  { key: "last14Days", label: "Last 14 Days" },
  { key: "lastMonth", label: "Last Month" },
  { key: "last3Months", label: "Last 3 Months" },
  { key: "last6Months", label: "Last 6 Months" },
];

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const toDateParam = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateRangeForFilter = (filterKey) => {
  if (filterKey === "all") {
    return {};
  }

  const now = new Date();
  let startDate = now;

  if (filterKey === "yesterday") startDate = addDays(now, -1);
  if (filterKey === "lastWeek") startDate = addDays(now, -6);
  if (filterKey === "last14Days") startDate = addDays(now, -13);
  if (filterKey === "lastMonth") startDate = addDays(now, -29);
  if (filterKey === "last3Months") startDate = addDays(now, -89);
  if (filterKey === "last6Months") startDate = addDays(now, -179);

  return {
    startDate: toDateParam(startDate),
    endDate: toDateParam(now),
  };
};

const getOrderDateValue = (order) => {
  const raw =
    order?.orderDate ||
    order?.orderDateTime ||
    order?.orderHistDate ||
    order?.createdAt ||
    order?.createdDate ||
    order?.createdOn ||
    order?.created;

  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatOrderDateTime = (date) => {
  if (!date) return "-";
  return `${date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} ${date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const OrderTabs = () => {
  const [value, setValue] = useState("placed");
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("today");

  const [visibleCount, setVisibleCount] = useState(LOAD_COUNT);

  const [selectedOrder, setSelectedOrder] = useState(null);

  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const [selectedOrderHistoryId, setSelectedOrderHistoryId] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const [printOpen, setPrintOpen] = useState(false);

  const [orderApprove] = useOrderApproveMutation();

  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);

  const { symbol: currencySymbol = "" } = getCurrency();

  const selectedTab = useMemo(
    () => ORDER_TABS.find((item) => item.value === value),
    [value],
  );

  const dateRangeParams = useMemo(
    () => getDateRangeForFilter(dateFilter),
    [dateFilter],
  );

  const ordersQueryArgs = useMemo(
    () => ({
      statusId: selectedTab?.statusId,
      ...dateRangeParams,
    }),
    [selectedTab?.statusId, dateRangeParams],
  );

  const { data, error, isLoading, isFetching, isError } = useGetOrdersByStatusQuery(
    ordersQueryArgs,
    {
      skip: !ordersQueryArgs.statusId,
    },
  );

  const orders = useMemo(() => {
    if (Array.isArray(data?.list)) return data.list;
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const errorMessage =
    error?.data?.message ||
    error?.data?.error ||
    error?.error ||
    "Unable to load orders";

  useEffect(() => {
    setVisibleCount(LOAD_COUNT);
    setQuery("");
    setDateFilter("today");

    // clear previous selected data
    setSelectedOrder(null);
    setSelectedOrderId(null);
    setSelectedOrderHistoryId(null);

    setDetailsOpen(false);
    setPrintOpen(false);
  }, [value]);

  const filteredOrders = useMemo(() => {
    const term = query.trim().toLowerCase();

    return orders.filter((order) => {
      if (!term) {
        return true;
      }

      const orderNo = String(order.orderHistId || order.orderHistoryId || "").toLowerCase();
      const customer = String(order.custName || "").toLowerCase();
      const mobile = String(order.mobile || order.mobileDel || "").toLowerCase();
      const dateText = formatOrderDateTime(getOrderDateValue(order)).toLowerCase();
      const itemMatch = (order.items || []).some((item) =>
        String(item.title || "").toLowerCase().includes(term),
      );

      return (
        orderNo.includes(term) ||
        customer.includes(term) ||
        mobile.includes(term) ||
        dateText.includes(term) ||
        itemMatch
      );
    });
  }, [orders, query]);

  const visibleOrders = useMemo(() => {
    return filteredOrders.slice(0, visibleCount);
  }, [filteredOrders, visibleCount]);

  const rowSummary = useMemo(() => {
    const totalAmount = filteredOrders.reduce(
      (sum, order) => sum + Number(order.price || 0),
      0,
    );
    const totalItems = filteredOrders.reduce(
      (sum, order) => sum + Number(order.totalQty || 0),
      0,
    );

    return {
      count: filteredOrders.length,
      amount: totalAmount,
      items: totalItems,
    };
  }, [filteredOrders]);

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + LOAD_COUNT);
  };

  const getHistoryId = (order) => {
    return order.orderHistId || order.orderHistoryId;
  };

  const openOrderDialog = (order) => {
    setSelectedOrder(order);
    setSelectedOrderId(order.orderHeadId);

    setSelectedOrderHistoryId(getHistoryId(order));

    setDetailsOpen(true);
  };

  const openPrintDialog = (order) => {
    setSelectedOrder(order);
    setSelectedOrderId(order.orderHeadId);

    setSelectedOrderHistoryId(getHistoryId(order));

    setPrintOpen(true);
  };

  const openDeliverDialog = (order) => {
    setSelectedOrder(order);

    setSelectedOrderHistoryId(getHistoryId(order));

    setDeliverDialogOpen(true);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid #dce4f2",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1,
          borderBottom: "1px solid #dce4f2",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              background: "#2f6bff",
              height: 3,
              borderRadius: 3,
            },

            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 800,
              color: "#6b7f9d",
              minHeight: 50,
            },

            "& .Mui-selected": {
              color: "#1e3457 !important",
            },
          }}
        >
          {ORDER_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
      </Box>

      <Box key={value} sx={{ p: 2 }}>
          <div className={styles.toolbarRow}>
            <TextField
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search order, customer, item or date"
              className={styles.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <div className={styles.summaryStrip}>
              <span>Orders: {rowSummary.count}</span>
              <span>Items: {rowSummary.items}</span>
              <span>
                Value: {currencySymbol} {rowSummary.amount.toFixed(2)}
              </span>
            </div>
          </div>

          <div className={styles.filterChipsRow}>
            {DATE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                className={`${styles.dateChip} ${
                  dateFilter === filter.key ? styles.dateChipActive : ""
                }`}
                onClick={() => setDateFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {isLoading || isFetching ? (
            <div className={styles.loading}>
              <CircularProgress />
            </div>
          ) : isError ? (
            <Typography color="error">{errorMessage}</Typography>
          ) : filteredOrders.length === 0 ? (
            <div className={styles.emptyState}>No orders available</div>
          ) : (
            <>
              <div className={styles.orderTableWrap}>
                <div className={styles.orderTableHead}>
                  <span>Order</span>
                  <span>Customer</span>
                  <span>Date & Time</span>
                  <span>Items</span>
                  <span>Total</span>
                  <span>Status</span>
                  <span>Actions</span>
                </div>

                {visibleOrders.map((order) => (
                  <article key={order.orderHeadId} className={styles.orderRow}>
                    <div className={styles.cellOrder}>
                      <div>
                        <p className={styles.orderNo}>#{order.orderHistId}</p>
                        <p className={styles.metaText}>#{order.orderHeadId}</p>
                      </div>
                    </div>

                    <div className={styles.cellCustomer}>
                      <p className={styles.customerName}>{order.custName || "Walk-in"}</p>
                      <p className={styles.metaText}>{order.mobile || order.mobileDel || "-"}</p>
                    </div>

                    <div className={styles.cellDate}>
                      <p className={styles.dateValue}>
                        {formatOrderDateTime(getOrderDateValue(order))}
                      </p>
                    </div>

                    <div className={styles.cellItems}>
                      <p className={styles.itemCount}>{Number(order.totalQty || 0)} item(s)</p>
                      <p className={styles.metaText}>
                        {(order.items || [])
                          .slice(0, 2)
                          .map((item) => item.title)
                          .join(", ") || "-"}
                        {(order.items || []).length > 2 ? "..." : ""}
                      </p>
                    </div>

                    <div className={styles.cellTotal}>
                      <b>
                        {currencySymbol} {Number(order.price || 0).toFixed(2)}
                      </b>
                    </div>

                    <div className={styles.cellStatus}>
                      <span className={`${styles.statusPill} ${styles[`status_${value}`]}`}>
                        {selectedTab.label}
                      </span>
                    </div>

                    <div className={styles.cellActions}>
                      <div className={styles.footerActions}>
                        <Tooltip title="View Order">
                          <IconButton
                            className={styles.iconBtn}
                            onClick={() => openOrderDialog(order)}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Reprint Bill">
                          <IconButton
                            className={styles.iconBtn}
                            onClick={() => openPrintDialog(order)}
                          >
                            <PrintOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {value === "approved" && (
                          <Tooltip title="Mark as Delivered">
                            <IconButton
                              className={`${styles.iconBtn} ${styles.deliverBtn}`}
                              onClick={() => openDeliverDialog(order)}
                            >
                              <LocalShippingOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {visibleCount < filteredOrders.length && (
                <div className={styles.loadMoreWrapper}>
                  <button
                    className={styles.loadMoreBtn}
                    onClick={handleLoadMore}
                  >
                    Load More Orders
                    <span>
                      ({visibleOrders.length}/{filteredOrders.length})
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
      </Box>

      <OrderDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        cartId={selectedOrderId}
        orderHistId={selectedOrderHistoryId}
        selectedOrder={selectedOrder}
        onReprint={() => {
          if (selectedOrder) {
            setDetailsOpen(false);
            openPrintDialog(selectedOrder);
          }
        }}
        from="admin"
        value={selectedTab.statusId}
      />

      <PrintOrderDetailsDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        orderId={selectedOrderId}
        orderHistId={selectedOrderHistoryId}
        from="admin"
        value={selectedTab.statusId}
      />

      <DeliverConfirmDialog
        open={deliverDialogOpen}
        onClose={() => setDeliverDialogOpen(false)}
        orderHistId={selectedOrderHistoryId}
        orderApprove={orderApprove}
        onFinal={() => {
          setDeliverDialogOpen(false);
        }}
      />
    </Paper>
  );
};

export default OrderTabs;
